import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTheme } from "context/ThemeContext";


import { apiBaseUrl } from '../../../services/config';
const API = apiBaseUrl;
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }});

const statusCfg = (T) => ({
  Pending:  { color:T.amber, bg:`${T.amber}1A`  },
  Approved: { color:T.green, bg:`${T.green}1A`  },
  Rejected: { color:T.red,   bg:`${T.red}1A` },
});

const inp = (T, x={}) => ({
  width:"100%",padding:"9px 12px",border:`1px solid ${T.border}`,borderRadius:8,
  background:T.inp_bg,color:T.text,
  fontFamily:"Outfit,sans-serif",fontSize:13,outline:"none",boxSizing:"border-box",...x
});

// Get today's date as YYYY-MM-DD
const today    = () => new Date().toISOString().split("T")[0];
const dayName  = (d) => new Date(d+"T00:00:00").toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"short",year:"numeric"});
const isWeekend= (d) => { const day=new Date(d+"T00:00:00").getDay(); return day===0||day===6; };

export default function CompOffEmployee({ employeeId }) {
  const { T, theme } = useTheme();
  const STATUS_CFG = statusCfg(T);
  const _u    = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
  const pid   = _u.id;   // platform_user_id — backend resolves employee from this

  const [tab,      setTab]      = useState("apply");
  const [balance,  setBalance]  = useState(null);
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [submitting,setSubmitting]=useState(false);
  const [toast,    setToast]    = useState("");
  const [form,     setForm]     = useState({ worked_date: today(), reason:"", is_half_day:false });

  const flash = (msg, ok=true) => {
    const text = Array.isArray(msg) ? msg.map(e => e.msg || JSON.stringify(e)).join(", ")
               : (typeof msg === "object" && msg !== null) ? (msg.msg || JSON.stringify(msg))
               : String(msg);
    setToast({ msg: text, ok });
    setTimeout(() => setToast(""), 4000);
  };

  const loadBalance = async () => {
    try {
      const r = await axios.get(`${API}/comp-off/balance?platform_user_id=${pid}`, auth());
      setBalance(r.data);
    } catch(e) {}
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/comp-off/?platform_user_id=${pid}`, auth());
      setHistory(r.data);
    } catch(e) {}
    finally { setLoading(false); }
  };

  useEffect(() => { if (pid) { loadBalance(); loadHistory(); } }, [pid]);

  const submit = async () => {
    if (!form.worked_date) return flash("Please select the date you worked", false);
    if (!form.reason.trim()) return flash("Please provide a reason", false);
    setSubmitting(true);
    try {
      await axios.post(`${API}/comp-off/?platform_user_id=${pid}`, form, auth());
      flash("✅ Comp-off request submitted! HR will review shortly.");
      setForm({ worked_date: today(), reason:"", is_half_day:false });
      loadHistory(); loadBalance();
      setTab("history");
    } catch(e) { flash(e.response?.data?.detail || "Failed to submit", false); }
    finally { setSubmitting(false); }
  };

  const withdraw = async (id) => {
    if (!window.confirm("Withdraw this comp-off request?")) return;
    try {
      await axios.delete(`${API}/comp-off/${id}?platform_user_id=${pid}`, auth());
      flash("Request withdrawn"); loadHistory(); loadBalance();
    } catch(e) { flash(e.response?.data?.detail || "Failed", false); }
  };

  return (
    <div style={{fontFamily:"Outfit,sans-serif",color:T.text}}>
      {/* Balance cards */}
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        <div style={{padding:"16px 20px",background:`linear-gradient(135deg,${T.green}1F,${T.cyan}14)`,border:`1px solid ${T.green}33`,borderRadius:12,flex:1,minWidth:130}}>
          <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Available</div>
          <div style={{fontSize:32,fontWeight:900,color:T.green}}>{balance?.available||0}</div>
          <div style={{fontSize:11,color:T.dim,marginTop:2}}>days to redeem</div>
        </div>
        <div style={{padding:"16px 20px",background:T.card,border:`1px solid ${T.border}`,borderRadius:12,flex:1,minWidth:130}}>
          <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Total Earned</div>
          <div style={{fontSize:32,fontWeight:900,color:T.cyan}}>{balance?.total_earned||0}</div>
          <div style={{fontSize:11,color:T.dim,marginTop:2}}>approved comp-offs</div>
        </div>
        <div style={{padding:"16px 20px",background:T.card,border:`1px solid ${T.border}`,borderRadius:12,flex:1,minWidth:130}}>
          <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Used</div>
          <div style={{fontSize:32,fontWeight:900,color:T.purple}}>{balance?.used||0}</div>
          <div style={{fontSize:11,color:T.dim,marginTop:2}}>days taken</div>
        </div>
        <div style={{padding:"16px 20px",background:T.card,border:`1px solid ${T.border}`,borderRadius:12,flex:1,minWidth:130}}>
          <div style={{fontSize:10,color:T.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Pending</div>
          <div style={{fontSize:32,fontWeight:900,color:T.amber}}>{balance?.pending_requests||0}</div>
          <div style={{fontSize:11,color:T.dim,marginTop:2}}>awaiting approval</div>
        </div>
      </div>

      {/* Info banner */}
      <div style={{padding:"12px 16px",background:T.cyan_faint,border:`1px solid ${T.border}`,borderRadius:9,marginBottom:20,fontSize:12.5,color:T.muted,lineHeight:1.7}}>
        <strong style={{color:T.cyan}}>ℹ️ How Comp-Off works:</strong> If you worked on a <strong style={{color:T.text}}>weekend or public holiday</strong>, submit a request below. Once HR approves, <strong style={{color:T.green}}>1 day (or 0.5 for half-day)</strong> is added to your leave balance. You can then apply a Comp-Off leave from the Leave Requests section.
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,borderBottom:`1px solid ${T.border}`,marginBottom:20}}>
        {[["apply","📋 Apply Comp-Off"],["history","🕐 My Requests"]].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)}
            style={{padding:"9px 18px",borderRadius:"8px 8px 0 0",border:`1px solid ${tab===k?T.border:"transparent"}`,borderBottom:"none",background:tab===k?T.card:T.card2,color:tab===k?T.cyan:T.muted,cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:700,marginBottom:tab===k?-1:0}}>
            {l}
          </button>
        ))}
      </div>

      {/* APPLY TAB */}
      {tab==="apply" && (
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:"0 12px 12px 12px",padding:24,maxWidth:520}}>
          <h3 style={{fontSize:15,fontWeight:800,margin:"0 0 18px",color:T.text}}>Submit Comp-Off Request</h3>

          {/* Date picker */}
          <div style={{marginBottom:16}}>
            <label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.07em"}}>Date You Worked *</label>
            <input type="date" value={form.worked_date} max={today()}
              onChange={e=>setForm(f=>({...f,worked_date:e.target.value}))}
              style={inp(T,{colorScheme:theme})}/>
            {form.worked_date && (
              <div style={{marginTop:6,fontSize:12,display:"flex",alignItems:"center",gap:6}}>
                <span style={{color:isWeekend(form.worked_date)?T.green:T.amber,fontWeight:700}}>
                  {isWeekend(form.worked_date)?"✓ Weekend":"⚠ Weekday"}
                </span>
                <span style={{color:T.dim}}>— {dayName(form.worked_date)}</span>
              </div>
            )}
          </div>

          {/* Half day toggle */}
          <div style={{marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
            <div style={{position:"relative",width:40,height:22,cursor:"pointer"}}
              onClick={()=>setForm(f=>({...f,is_half_day:!f.is_half_day}))}>
              <div style={{position:"absolute",inset:0,borderRadius:11,
                background:form.is_half_day?T.cyan:T.toggle_bg,transition:"background 0.2s"}}/>
              <div style={{position:"absolute",top:2,left:form.is_half_day?20:2,width:18,height:18,
                borderRadius:"50%",background:"white",transition:"left 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.3)"}}/>
            </div>
            <label style={{fontSize:13,color:T.text,cursor:"pointer"}}
              onClick={()=>setForm(f=>({...f,is_half_day:!f.is_half_day}))}>
              Half Day <span style={{fontSize:11,color:T.dim}}>(credits 0.5 days)</span>
            </label>
          </div>

          {/* Reason */}
          <div style={{marginBottom:20}}>
            <label style={{fontSize:11,fontWeight:700,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.07em"}}>Reason / Work Done *</label>
            <textarea value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))}
              placeholder="e.g. Worked on production deployment, attended client call, project deadline..."
              rows={3} style={{...inp(T),resize:"vertical"}}/>
          </div>

          <button onClick={submit} disabled={submitting||!form.reason.trim()}
            style={{width:"100%",padding:"11px",borderRadius:9,border:"none",cursor:submitting?"not-allowed":"pointer",
              background:`linear-gradient(135deg,${T.cyan},${T.purple})`,color:"#fff",
              fontFamily:"Outfit,sans-serif",fontSize:14,fontWeight:800,
              opacity:(submitting||!form.reason.trim())?0.6:1}}>
            {submitting?"Submitting…":"Submit Comp-Off Request →"}
          </button>
        </div>
      )}

      {/* HISTORY TAB */}
      {tab==="history" && (
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:"0 12px 12px 12px",overflow:"hidden"}}>
          {loading && <div style={{padding:32,textAlign:"center",color:T.muted}}>Loading…</div>}
          {!loading && history.length===0 && (
            <div style={{padding:48,textAlign:"center",color:T.dim}}>
              <div style={{fontSize:32,marginBottom:12}}>📋</div>
              <div style={{fontSize:14,fontWeight:600}}>No comp-off requests yet</div>
              <div style={{fontSize:13,marginTop:4}}>Submit your first request from the Apply tab</div>
            </div>
          )}
          {history.map(r=>(
            <div key={r.id} style={{padding:"16px 20px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"flex-start",gap:16}}>
              {/* Date badge */}
              <div style={{textAlign:"center",padding:"10px 14px",background:T.inp_bg,border:`1px solid ${T.border}`,borderRadius:10,flexShrink:0,minWidth:80}}>
                <div style={{fontSize:11,color:T.dim,marginBottom:2}}>{new Date(r.worked_date+"T00:00:00").toLocaleDateString("en-IN",{month:"short"})}</div>
                <div style={{fontSize:22,fontWeight:900,color:T.text,lineHeight:1}}>{new Date(r.worked_date+"T00:00:00").getDate()}</div>
                <div style={{fontSize:10,color:T.dim,marginTop:2}}>{new Date(r.worked_date+"T00:00:00").toLocaleDateString("en-IN",{weekday:"short"})}</div>
              </div>

              {/* Details */}
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5,flexWrap:"wrap"}}>
                  <span style={{padding:"3px 10px",borderRadius:100,fontSize:11,fontWeight:700,
                    background:STATUS_CFG[r.status]?.bg,color:STATUS_CFG[r.status]?.color}}>
                    {r.status}
                  </span>
                  <span style={{padding:"3px 9px",borderRadius:100,fontSize:11,fontWeight:700,
                    background:r.is_half_day?`${T.purple}26`:`${T.cyan}1A`,
                    color:r.is_half_day?T.purple:T.cyan}}>
                    {r.is_half_day?"Half Day • 0.5d":"Full Day • 1d"}
                  </span>
                </div>
                <div style={{fontSize:13,color:T.muted,marginBottom:3}}>{r.reason}</div>
                {r.remarks && <div style={{fontSize:12,color:T.dim,marginTop:4}}>HR remarks: {r.remarks}</div>}
                <div style={{fontSize:11,color:T.dim,marginTop:5}}>
                  Submitted {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : ""}
                  {r.approved_at && ` · ${r.status} ${new Date(r.approved_at).toLocaleDateString("en-IN",{day:"numeric",month:"short"})}`}
                </div>
              </div>

              {/* Withdraw */}
              {r.status==="Pending" && (
                <button onClick={()=>withdraw(r.id)}
                  style={{padding:"6px 12px",borderRadius:7,border:`1px solid ${T.red}33`,background:`${T.red}1A`,color:T.red,cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:11,fontWeight:700,flexShrink:0}}>
                  Withdraw
                </button>
              )}
              {r.status==="Approved" && (
                <div style={{padding:"6px 12px",borderRadius:7,background:`${T.green}14`,border:`1px solid ${T.green}33`,fontSize:11,fontWeight:700,color:T.green,flexShrink:0}}>
                  ✓ Credited
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div style={{position:"fixed",bottom:24,right:24,zIndex:600,padding:"12px 20px",borderRadius:10,
          background:toast.ok?`${T.green}15`:`${T.red}15`,
          border:`1px solid ${toast.ok?T.green:T.red}40`,
          color:toast.ok?T.green:T.red,fontSize:13,fontWeight:700,
          boxShadow:T.shadow}}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}